import { isDefined } from "@clipboard-health/util-ts";

import { type HiringFeedWithDetails } from "../../HiringFeed/api/usePaginatedHiringFeed";
import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import { type PlacementData } from "../types/placement";

export function hiringFeedToPlacement(hiringFeed: HiringFeedWithDetails): PlacementData {
  return {
    id: hiringFeed.id,
    status: hiringFeed.status,
    title: hiringFeed.title,
    workerTypes: hiringFeed.workerTypes,
    jobShifts: hiringFeed.jobShifts,
    payRange: hiringFeed.payRange,
    perks: hiringFeed.perks,
    promotion: hiringFeed.promotion,
    interviewSettings: hiringFeed.interviewSettings,
    interviews: [],
    distance: hiringFeed.distance,
    isLicenseEligible: hiringFeed.meta.isLicenseEligible,
    applications: undefined,
    workerRequirements: hiringFeed.workerRequirements,
    credentialRequirements: hiringFeed.credentialRequirements,
    tags: hiringFeed.tags,
    workplace: {
      id: hiringFeed.workplace.id,
      name: hiringFeed.workplace.name,
      neighborhood: hiringFeed.workplace.address.neighborhood,
    },
    webApplicationLink: hiringFeed.webApplicationLink,
    allowRequestInterview: hiringFeed.meta.allowRequestInterview ?? false,
    score: hiringFeed.meta.score,
    scoreBreakdown: hiringFeed.meta.scoreBreakdown,
    baseRate: hiringFeed.meta.baseRate,
    totalRate: hiringFeed.meta.totalRate,
    totalRateBreakdown: hiringFeed.meta.totalRateBreakdown,
    matchedType: hiringFeed.meta.matchedType,
    applicationMethod: hiringFeed.meta.applicationMethod,
    transparencyPayRates: hiringFeed.transparencyPayRates,
    isRecruiterScreeningEnabled: hiringFeed.meta.isRecruiterScreeningEnabled,
    isEasyScheduling: hiringFeed.meta.isEasyScheduling,
    interviewInstructions: hiringFeed.interviewInstructions,
    isFavorite: hiringFeed.meta.isFavorited ?? false,
    workerFavoritePlacementId: hiringFeed.workerFavoritePlacementId,
  };
}

export function placementWithDetailsToData(
  placementWithDetails: PlacementWithDetails,
  credentialRequirementConfiguration: Set<string>
): PlacementData {
  const allRequirements =
    placementWithDetails.workerRequirements?.map((workerRequirement) => ({
      ...workerRequirement.attributes,
      id: workerRequirement.id,
      requirement: {
        ...workerRequirement.relationships.requirement.data.attributes,
        id: workerRequirement.relationships.requirement.data.id,
      },
    })) ?? [];

  const workerRequirements = allRequirements.filter(
    (requirement) => !credentialRequirementConfiguration.has(requirement.requirement.id)
  );

  const credentialRequirements = allRequirements.filter((requirement) =>
    credentialRequirementConfiguration.has(requirement.requirement.id)
  );

  return {
    id: placementWithDetails.id,
    status: placementWithDetails.status,
    title: placementWithDetails.title,
    workerTypes: placementWithDetails.workerTypes,
    jobShifts: placementWithDetails.jobShifts,
    payRange: placementWithDetails.payRange,
    perks: placementWithDetails.perks,
    interviewSettings: {
      scheduleInterviews:
        placementWithDetails.interviewSettings?.attributes.scheduleInterviews ?? false,
      availability: placementWithDetails.interviewSettings?.attributes.availability ?? [],
      id: placementWithDetails.workplace.id,
    },
    distance: placementWithDetails.distance,
    isLicenseEligible: placementWithDetails.isLicenseEligible,
    applications: placementWithDetails.applications,
    workerRequirements,
    credentialRequirements,
    tags: placementWithDetails.tags,
    workplace: {
      id: placementWithDetails.workplace.id,
      name: placementWithDetails.workplace.name,
    },
    interviews: placementWithDetails.interviews,
    webApplicationLink: placementWithDetails.webApplicationLink,
    allowRequestInterview: placementWithDetails.allowRequestInterview ?? false,
    score: placementWithDetails.score,
    scoreBreakdown: placementWithDetails.scoreBreakdown,
    baseRate: placementWithDetails.baseRate,
    totalRate: placementWithDetails.totalRate,
    totalRateBreakdown: placementWithDetails.totalRateBreakdown,
    matchedType: placementWithDetails.matchedType,
    applicationMethod: placementWithDetails.applicationMethod,
    transparencyPayRates: placementWithDetails.transparencyPayRates,
    isRecruiterScreeningEnabled: placementWithDetails.isRecruiterScreeningEnabled,
    isEasyScheduling: placementWithDetails.isEasyScheduling,
    interviewInstructions: placementWithDetails.interviewInstructions,
    isFavorite: placementWithDetails.isFavorited ?? false,
    workerFavoritePlacementId: placementWithDetails.workerFavoritePlacementId,
  };
}

export function isVerifiedPlacement(
  placement: Pick<PlacementData | PlacementWithDetails, "webApplicationLink">
): boolean {
  const { webApplicationLink } = placement;
  return !isDefined(webApplicationLink) || webApplicationLink.trim() === "";
}
